/**
 * Starting and ending a view-as session and a break-glass grant. These are the only places that
 * call the three write endpoints and then update the local store, so the header logic in the API
 * client and the banners always agree with what the server holds.
 */
import type { QueryClient } from '@tanstack/react-query'

import {
  endBreakglass,
  endImpersonation,
  grantBreakglass,
  startImpersonation,
} from './api'
import {
  clearViewAs,
  getViewAs,
  isLive,
  setBreakglass,
  setViewAs,
  type ViewAsState,
} from './view-as'

/** Used when a session ends from a banner, which has no room for a reason of its own. */
export const BANNER_END_REASON = 'Ended from the console banner.'

/** Everything cached for the account being viewed is wrong for the next view; the staff cache is not. */
export function dropConsoleCache(client: QueryClient): void {
  client.removeQueries({ predicate: (query) => query.queryKey[0] !== 'staff' })
}

export async function beginViewAs(
  client: QueryClient,
  account: { id: string; name: string },
  reason: string,
  staffTier: string | null,
): Promise<ViewAsState> {
  const existing = getViewAs()
  if (existing !== null && existing.accountId !== account.id) {
    await finishViewAs(client, BANNER_END_REASON)
  }
  const current = getViewAs()
  if (current !== null && current.accountId === account.id && isLive(current.expiresAt)) {
    return current
  }
  const session = await startImpersonation(account.id, reason)
  const state: ViewAsState = {
    accountId: account.id,
    accountName: account.name,
    impersonationId: session.impersonation_id,
    expiresAt: session.expires_at,
    staffTier,
    breakglass: null,
  }
  setViewAs(state)
  dropConsoleCache(client)
  return state
}

/** Ends the grant (if any) and then the session; local state is cleared even if the server says no. */
export async function finishViewAs(client: QueryClient, reason: string): Promise<void> {
  const state = getViewAs()
  if (state === null) return
  try {
    if (state.breakglass !== null && isLive(state.breakglass.expiresAt)) {
      await endBreakglass(state.breakglass.grantId, reason).catch(() => undefined)
    }
    if (isLive(state.expiresAt)) await endImpersonation(state.impersonationId, reason)
  } finally {
    clearViewAs()
    dropConsoleCache(client)
  }
}

/**
 * Break-glass on one zone (S6). The history endpoints need both a grant on the zone and a view-as
 * session on its account (S5), so this starts the session if the caller does not hold one on that
 * account, then takes the grant. If the grant is refused, a session started here is ended again.
 */
export async function beginBreakglass(
  client: QueryClient,
  zone: { id: string; accountId: string; accountName: string },
  reason: string,
  staffTier: string | null,
): Promise<ViewAsState> {
  const before = getViewAs()
  const hadSession = before !== null && before.accountId === zone.accountId && isLive(before.expiresAt)
  await beginViewAs(client, { id: zone.accountId, name: zone.accountName }, reason, staffTier)
  try {
    const grant = await grantBreakglass(zone.id, reason)
    setBreakglass({ grantId: grant.grant_id, zoneId: grant.zone_id, expiresAt: grant.expires_at })
  } catch (error) {
    if (!hadSession) await finishViewAs(client, BANNER_END_REASON).catch(() => undefined)
    throw error
  }
  dropConsoleCache(client)
  const after = getViewAs()
  if (after === null) throw new Error('The view-as session disappeared.')
  return after
}

/** Ends only the grant; the read-only view stays. */
export async function finishBreakglass(client: QueryClient, reason: string): Promise<void> {
  const state = getViewAs()
  if (state === null || state.breakglass === null) return
  try {
    if (isLive(state.breakglass.expiresAt)) await endBreakglass(state.breakglass.grantId, reason)
  } finally {
    setBreakglass(null)
    dropConsoleCache(client)
  }
}
